import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { OrganizationPartnerItemFragment } from '@client/graphql/__generated__/types';
import { ArrowLeft, MoreVertical } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useNotificationTemplateMutations } from '../../hooks/useNotificationTemplateMutations';

interface PartnerHeaderProps {
  partner: OrganizationPartnerItemFragment;
  draftCount?: number;
  unapprovedCount?: number;
}

export const PartnerHeader = ({
  partner,
  draftCount = 0,
  unapprovedCount = 0,
}: PartnerHeaderProps) => {
  const navigate = useNavigate();
  const { publishTemplates, publishing } = useNotificationTemplateMutations();

  const handleBack = () => {
    navigate('/organizations/notifications/pipeline');
  };

  const handlePublish = () => {
    void publishTemplates(partner.id);
  };

  const hasTemplatesToPublish = draftCount > 0;

  return (
    <Flex
      alignItems="center"
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
      height="69px"
      justifyContent="space-between"
      px={5}
    >
      {/* Back Link */}
      <Flex
        _hover={{ opacity: 0.8 }}
        alignItems="center"
        cursor="pointer"
        gap={2}
        transition="opacity 0.2s"
        onClick={handleBack}
      >
        <Icon as={ArrowLeft} boxSize={4} color="whiteAlpha.600" />
        <Text color="whiteAlpha.600">Pipeline</Text>
      </Flex>

      {/* Right Section */}
      <HStack spacing={4}>
        {/* Partner Chip */}
        <Flex
          alignItems="center"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
          borderRadius="sm"
          gap={2}
          px={2}
          py={2}
        >
          {partner.logo?.url && (
            <Box bg="white" borderRadius="sm" overflow="hidden" p={1}>
              <Image
                alt={partner.name}
                height="24px"
                objectFit="contain"
                src={partner.logo.url}
              />
            </Box>
          )}
          <Text color="white" fontWeight="medium">
            {partner.name}
          </Text>
          <Menu>
            <MenuButton
              _hover={{ bg: 'whiteAlpha.100' }}
              aria-label="Partner options"
              as={IconButton}
              icon={<Icon as={MoreVertical} boxSize={4} />}
              size="xs"
              variant="ghost"
            />
            <MenuList bg="indigo.700" borderColor="whiteAlpha.100">
              {/* TODO: Implement edit partner functionality */}
              <MenuItem
                isDisabled
                _hover={{ bg: 'whiteAlpha.100' }}
                bg="transparent"
              >
                Edit Partner
              </MenuItem>
              {/* TODO: Implement delete partner functionality */}
              <MenuItem
                isDisabled
                _hover={{ bg: 'whiteAlpha.100' }}
                bg="transparent"
                color="red.400"
              >
                Delete Partner
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>

        {/* Status Badges */}
        <HStack spacing={2}>
          {draftCount > 0 && (
            <Flex
              alignItems="center"
              bg="whiteAlpha.50"
              borderRadius="full"
              gap={2}
              px={3}
              py={2}
            >
              <Box
                bg="orange.400"
                borderRadius="full"
                height="8px"
                width="8px"
              />
              <Text color="white" fontSize="sm">
                Draft
              </Text>
            </Flex>
          )}
          {unapprovedCount > 0 && (
            <Flex
              alignItems="center"
              bg="whiteAlpha.50"
              borderRadius="full"
              gap={2}
              px={3}
              py={2}
            >
              <Box
                bg="orange.400"
                borderRadius="full"
                height="8px"
                width="8px"
              />
              <Text color="white" fontSize="sm">
                Not approved
              </Text>
            </Flex>
          )}

          {/* Publish Button */}
          <Button
            _hover={{ bg: 'cyan.400' }}
            bg="cyan.500"
            color="indigo.900"
            isDisabled={!hasTemplatesToPublish}
            isLoading={publishing}
            opacity={hasTemplatesToPublish ? 1 : 0.3}
            size="sm"
            onClick={handlePublish}
          >
            Publish
          </Button>
        </HStack>
      </HStack>
    </Flex>
  );
};
